import { trigger } from '@angular/animations';
import { NgIf } from '@angular/common';
import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { fadeOutIf } from 'src/app/animations/fade-out.if.animation';
import { fadeIf } from 'src/app/animations/fade.if.animation';
import { Rect } from 'src/app/classes/rect.class';
import { ScreenSizes } from 'src/app/classes/screen-sizes.class';
import { sleep } from 'src/app/lib/sleep';
import { waitForImageLoad } from 'src/app/lib/wait-for-image-load';
import { StateService } from 'src/app/services/state.service';
import { AssetTransitionService, TransitionObj } from '../../../gallery/services/asset-transition.service';

const animationDuration = 200;

@Component({
  selector: 'app-asset-transitioner',
  templateUrl: './asset-transitioner.component.html',
  styleUrls: ['./asset-transitioner.component.scss'],
  animations: [trigger('transitionAssetBgIn', fadeIf), trigger('transitionAssetBgOut', fadeOutIf)],
  standalone: true,
  imports: [NgIf],
})
export class AssetTransitionerComponent implements OnDestroy {
  assetWatcher: Subscription;
  url: string;
  rect: Rect;
  showBackgroundIn = false;
  showBackgroundOut = false;
  @ViewChild('asset') asset: ElementRef<HTMLImageElement>;
  fadeOut = false;

  constructor(private assetTransition: AssetTransitionService, private router: Router, private state: StateService) {
    this.assetWatcher = this.assetTransition.onAssetChange.subscribe(obj => {
      if (obj) {
        this.transition(obj);
        this.assetTransition.clear();
      }
    });
  }

  private async transition(obj: TransitionObj) {
    this.rect = obj.from;
    if (obj.direction === 'to') await this.transitionToAsset(obj);
    else await this.transitionFromAsset(obj);
    this.rect = undefined;
    this.fadeOut = false;
  }

  private async transitionToAsset(obj: TransitionObj) {
    this.showBackgroundIn = true;

    const el = document.getElementById(`asset-${obj.asset.id}`);
    const imageEl = el?.querySelector('img');
    const videoEl = el?.querySelector('video');

    if (imageEl) {
      this.url = imageEl.src;
      await sleep(0);
      await this.onAssetLoad();
      await sleep(0);
    } else if (videoEl) {
      this.url = videoEl.poster;
      await sleep(0);
      await this.onAssetLoad();
      await sleep(0);
    }
    if (el) el.classList.add('transition-hide');
    this.rect = obj.to;

    await sleep(animationDuration);

    if (el) el.classList.remove('transition-hide');

    obj.resolve();
    await this.onRouteChangeSuccess();
    this.showBackgroundIn = false;
    await sleep(20);
    const img = <HTMLImageElement>document.querySelector('.asset-container.active img');
    if (img) {
      // Waiting for the asset on the asset details page to load or the
      const resp = await Promise.race([this.onRouteChangeSuccess(), Promise.all([waitForImageLoad(img, true)])]);
      if (!resp) return console.warn('Ending transition early because detected page change');
    } else {
      const video = <HTMLVideoElement>document.querySelector('.asset-container.active video');
      if (video) {
        await sleep(250);
      } else {
        console.warn('couldnt find img element');
        await sleep(150);
      }
    }
  }

  private async transitionFromAsset(obj: TransitionObj) {
    if (ScreenSizes.screenSize() !== 'mobile') this.showBackgroundOut = true;
    const el = document.querySelector(`#asset-${obj.asset.id}`);
    this.showBackgroundOut = false;
    if (!el) {
      console.warn('Couldnt find asset', obj.asset.id);
      this.fadeOut = true;
      return obj.resolve();
    }
    const imgEl = el.querySelector('img');
    const videoEl = el.querySelector('video');
    if (imgEl && imgEl.getAttribute('src')) {
      this.url = imgEl.getAttribute('src');
      await sleep(0);
      await this.onAssetLoad();
    }
    if (videoEl && videoEl.getAttribute('poster')) {
      this.url = videoEl.getAttribute('poster');
      await sleep(0);
      await this.onAssetLoad();
    } else {
      await sleep(0);
      await this.onAssetLoad();
    }
    el.classList.add('transition-hide');
    obj.resolve();
    await this.onRouteChangeSuccess();
    this.rect = Rect.fromDOMRect((imgEl || el).getBoundingClientRect());
    await sleep(animationDuration);
    if (el) el.classList.remove('transition-hide');
  }

  private async onAssetLoad() {
    if (!this.asset?.nativeElement) return;
    if (this.asset.nativeElement.complete) return;
    if (!this.url) return;
    return new Promise(resolve => {
      this.asset.nativeElement.onload = async () => {
        if (!this.asset?.nativeElement) return resolve(false);
        if (!this.asset.nativeElement?.decode) {
          // https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/decode
          await sleep(5);
        } else await this.asset.nativeElement.decode();
        window.requestAnimationFrame(resolve);
      };
    });
  }

  private onRouteChangeSuccess() {
    return new Promise(resolve => {
      const sub = this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          sub.unsubscribe();
          resolve(undefined);
        }
      });
    });
  }

  ngOnDestroy() {
    this.assetWatcher.unsubscribe();
  }
}
